import { CommonModule } from '@angular/common';
import { Component, ElementRef, Inject, NgZone, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Translations, TranslationService } from 'app/shared/services/translation.service';
import { Subscription } from 'rxjs';

export type ContractSignDialogResult = {
  fullName: string;
  signatureDataUrl: string; // data:image/png;base64,...
};

@Component({
  selector: 'app-contract-sign-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './contract-sign-dialog.component.html',
  styleUrls: ['./contract-sign-dialog.component.scss'],
})
export class ContractSignDialogComponent {
  @ViewChild('sigCanvas', { static: true }) sigCanvas!: ElementRef<HTMLCanvasElement>;

  fullName = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(2)],
  });

  private ctx!: CanvasRenderingContext2D;
  private drawing = false;
  private lastX = 0;
  private lastY = 0;
  private hasInk = false;
  private dpr = 1;
  private onWindowResize = () => this.resizeCanvas();

  translations!: Translations;
  private translationSubscription!: Subscription;

  constructor(
    public dialogRef: MatDialogRef<ContractSignDialogComponent, ContractSignDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) public data: { initialFullName?: string } | null,
    private translationService: TranslationService,
    private ngZone: NgZone
  ) {
    if (data?.initialFullName) this.fullName.setValue(data.initialFullName);
    this.translationSubscription = this.translationService.translations$.subscribe(
      (translations: Translations) => {
        this.ngZone.run(() => {
          this.translations = translations;
        });
      }
    );
  }

  ngAfterViewInit() {
    const canvas = this.sigCanvas.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    this.ctx = ctx;

    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111';

    // The dialog can change size after first paint; measure after layout.
    requestAnimationFrame(() => this.resizeCanvas());
    window.addEventListener('resize', this.onWindowResize);

    // Pointer events
    canvas.addEventListener('pointerdown', this.onPointerDown, { passive: false });
    canvas.addEventListener('pointermove', this.onPointerMove, { passive: false });
    canvas.addEventListener('pointerup', this.onPointerUp, { passive: false });
    canvas.addEventListener('pointercancel', this.onPointerUp, { passive: false });
    canvas.addEventListener('pointerleave', this.onPointerUp, { passive: false });
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
    if (this.translationSubscription) this.translationSubscription.unsubscribe();
  }

  clear() {
    const canvas = this.sigCanvas.nativeElement;
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width / this.dpr, canvas.height / this.dpr);
    this.hasInk = false;
  }

  save() {
    this.fullName.markAsTouched();
    if (this.fullName.invalid) return;
    if (!this.hasInk) return;

    const canvas = this.sigCanvas.nativeElement;
    const dataUrl = canvas.toDataURL('image/png');
    this.dialogRef.close({
      fullName: this.fullName.value.trim(),
      signatureDataUrl: dataUrl,
    });
  }

  close() {
    this.dialogRef.close(null);
  }

  onFullNameInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const currentValue = input.value.replace(/^\s+/, '');
    if (currentValue !== input.value) {
      this.fullName.setValue(currentValue, { emitEvent: false });
    }
  }

  get canSubmit(): boolean {
    return this.fullName.valid && this.hasInk;
  }

  private getCanvasPoint = (e: PointerEvent) => {
    const canvas = this.sigCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    return { x, y };
  };

  private onPointerDown = (e: PointerEvent) => {
    e.preventDefault();
    const canvas = this.sigCanvas.nativeElement;
    canvas.setPointerCapture(e.pointerId);
    const { x, y } = this.getCanvasPoint(e);
    this.drawing = true;
    this.lastX = x;
    this.lastY = y;
  };

  private onPointerMove = (e: PointerEvent) => {
    if (!this.drawing) return;
    e.preventDefault();
    const { x, y } = this.getCanvasPoint(e);
    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();
    this.lastX = x;
    this.lastY = y;
    this.hasInk = true;
  };

  private onPointerUp = (e: PointerEvent) => {
    if (!this.drawing) return;
    e.preventDefault();
    this.drawing = false;
  };

  private resizeCanvas() {
    const canvas = this.sigCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const nextDpr = Math.max(window.devicePixelRatio || 1, 1);
    const nextW = Math.floor(rect.width * nextDpr);
    const nextH = Math.floor(rect.height * nextDpr);

    // Avoid nuking strokes if nothing changed.
    if (canvas.width === nextW && canvas.height === nextH && this.dpr === nextDpr) return;

    // Save existing ink if any
    const snapshot = this.hasInk ? canvas.toDataURL('image/png') : null;

    this.dpr = nextDpr;
    canvas.width = nextW;
    canvas.height = nextH;

    // Stable transform: 1 unit = 1 CSS pixel
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    if (snapshot) {
      const img = new Image();
      img.onload = () => {
        this.ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = snapshot;
    }
  }
}

